/**
 * Custom hook to get the employerId for the current user
 * @returns {number|string|null} The employer ID for the current user, or null if loading/not available
 * @throws {Error} If user has no employer assigned (non-admin) after claims are loaded
 */
import { useParams } from 'react-router-dom';
import useUser from '../services/useUser';
import useImpersonationSession from './useImpersonationSession';

function useEmployerId() {
  const { employerId: employerIdParam } = useParams();
  const user = useUser();
  const claims = user?.customClaims;
  const claimsLoading = user?.claimsLoading;
  // #380 §5 "the router narrows navigation to the target employer's routes
  // (useEmployerId is the existing seam)" — this hook is the one place every
  // employer-scoped screen already asks "which employer". A session pins the
  // answer to its own target, whatever the URL param says, so a support
  // admin can't wander into another employer's route by editing the URL.
  // This is UI convenience only (§3.1 row D) — the callable gate (A) and the
  // rules guard (C) are the real enforcement, not this hook.
  const impersonation = useImpersonationSession(user?.uid)

  // If claims are still loading, return null to indicate loading state
  // Components should handle this loading state appropriately
  if (claimsLoading || (user && claims === null)) {
    return null;
  }

  // Admin and payroll accountant both select the active employer from the
  // URL param. The accountant's access is scoped to their assigned group by
  // firestore.rules; the hook only resolves which employer is active.
  if (claims?.role === 'admin' || claims?.role === 'payrollAccountant') {
    if (impersonation.status === 'active') {
      // Stored as a string (functions/permissions/session.js) regardless of
      // the target employer's own doc-id shape — coerce the same way the
      // URL-param branch below does, so callers see the same type either way.
      const sessionEmployerId = impersonation.session.employerId;
      const parsedSessionEmployerId = parseInt(sessionEmployerId, 10);
      return isNaN(parsedSessionEmployerId) ? sessionEmployerId : parsedSessionEmployerId;
    }

    if (typeof employerIdParam === 'undefined' || employerIdParam === '' || employerIdParam == null) {
      return null;
    }
    const parsed = parseInt(employerIdParam, 10);
    // Keep backward compatibility: older/seeded employers can use non-numeric document IDs.
    // Numeric IDs are returned as numbers, non-numeric IDs are returned as-is.
    if (isNaN(parsed)) {
      return employerIdParam;
    }
    return parsed;
  }

  // Employee uses employer from claims
  if (claims?.role === 'employee' && claims?.employer) {
    return claims.employer;
  }

  // Non-admin/non-employee MUST have employer in claims
  if (!claims?.employer) {
    throw new Error('לא הוקצה מעסיק למשתמש - יש לפנות למנהל המערכת');
  }

  return claims.employer;
}

export default useEmployerId;
